import React, { useState } from "react";
import {
  Col,
  Button,
  FormGroup,
  Input,
  Card,
  CardHeader,
  CardBody,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import swal from "sweetalert";
import Papa from "papaparse";
import { LoadingOverlay } from "../../util/Helpers";
import { uploadCashDetails, getUploadCashDetailsByDateRange } from "../../util/APIUtils";

function CashDetailsCenter() {
  const [block, setBlock] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const todayDate = new Date().toISOString().slice(0, 10);
  const [parsedData, setParsedData] = useState([]);
  const [responseData, setResponseData] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});
  const [activeTab, setActiveTab] = useState("1");
  const [dateFilter, setDateFilter] = useState({
    frmDate: todayDate,
    toDate: todayDate,
  });
  const [dateResponseData, setDateResponseData] = useState([]);
  const [dateLoading, setDateLoading] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === "text/csv") {
        setSelectedFile(file);
      } else {
        swal({
          text: "Please select valid CSV file",
        });
        clearAll();
      }
    }
  };

  const clearAll = () => {
    setSelectedFile(null);
    setParsedData([]);
    setResponseData([]);
    setColumnFilters({});
    const fileInput = document.getElementById("file");
    if (fileInput) fileInput.value = "";
  };

  const handleUpload = () => {
    if (!selectedFile) {
      swal({ text: "Please select file" });
      return;
    }
    setBlock(true);
    Papa.parse(selectedFile, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        const data = result.data;
        setParsedData(data);
        uploadCashDetails({ data: data })
          .then((resp) => {
            setBlock(false);
            if (resp && resp.results && Array.isArray(resp.results)) {
              setResponseData(resp.results);
              swal({ text: resp.message || "Response received" });
              setSelectedFile(null);
              const fileInput = document.getElementById("file");
              if (fileInput) fileInput.value = "";
            }
          })
          .catch((err) => {
            setBlock(false);
            clearAll();
            swal({ text: "Unexpected error: " + err });
          });
      },
      error: (err) => {
        setBlock(false);
        swal({ text: "Error parsing CSV: " + err.message });
      },
    });
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setDateFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchByDate = () => {
    if (!dateFilter.frmDate || !dateFilter.toDate) {
      swal({ text: "Please select both from and to dates" });
      return;
    }
    setDateLoading(true);
    getUploadCashDetailsByDateRange(dateFilter)
      .then((resp) => {
        setDateLoading(false);
        const results = Array.isArray(resp) ? resp : (resp && Array.isArray(resp.results) ? resp.results : []);
        setDateResponseData(results);
        swal({ text: (resp && resp.message) ? resp.message : "Date search completed" });
      })
      .catch((err) => {
        setDateLoading(false);
        swal({ text: "Unexpected error: " + err });
      });
  };

  const renderTable = (data) => {
    if (!data || data.length === 0) return null;
    const headers = Object.keys(data[0]);

    // Filter data based on column filters
    const filteredData = data.filter((row) => {
      return headers.every((header) => {
        const filterValue = columnFilters[header] ? columnFilters[header].toLowerCase() : "";
        const cellValue = String(row[header] || "").toLowerCase();
        return cellValue.includes(filterValue);
      });
    });

    const handleFilterChange = (header, value) => {
      setColumnFilters((prev) => ({
        ...prev,
        [header]: value,
      }));
    };

    const exportToCSV = () => {
      if (filteredData.length === 0) {
        swal({ text: "No data to export" });
        return;
      }
      const csvContent = [
        headers.join(","),
        ...filteredData.map((row) =>
          headers.map((h) => {
            const value = row[h];
            // Escape quotes and wrap in quotes if contains comma or quotes
            const stringValue = String(value || "");
            if (stringValue.includes(",") || stringValue.includes('"')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `dues-verification-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <div>
        <div className="mb-3">
          <Button
            onClick={exportToCSV}
            style={{
              background: "#28a745",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              color: "#fff",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(40, 167, 69, 0.4)"
            }}
            onMouseOver={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(40, 167, 69, 0.6)";
            }}
            onMouseOut={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(40, 167, 69, 0.4)";
            }}
            size="sm"
          >
            <i className="fa fa-download" style={{ marginRight: "8px" }}></i>
            Export to CSV
          </Button>
        </div>
        <Table responsive bordered size="sm" style={{
          marginTop: "15px",
          borderRadius: "8px",
          overflow: "hidden"
        }}>
          <thead style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff"
          }}>
            <tr>
              {headers.map((h) => (
                <th key={h} style={{
                  color: "#fff",
                  fontWeight: "600",
                  padding: "12px 15px",
                  borderBottom: "none"
                }}>
                  {h}
                </th>
              ))}
            </tr>
            <tr style={{ background: "#f8f9fa" }}>
              {headers.map((h) => (
                <th key={"filter-" + h} style={{ padding: "8px 5px", borderTop: "none" }}>
                  <Input
                    type="text"
                    placeholder={`Search ${h}`}
                    value={columnFilters[h] || ""}
                    onChange={(e) => handleFilterChange(h, e.target.value)}
                    style={{
                      fontSize: "12px",
                      padding: "6px 8px",
                      borderRadius: "6px",
                      border: "1px solid #ddd"
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, idx) => (
              <tr key={idx} style={{
                background: idx % 2 === 0 ? "#fff" : "#f8f9fa",
                transition: "all 0.2s ease"
              }}
                onMouseOver={(e) => e.target.parentElement.style.background = "#f0f0f5"}
                onMouseOut={(e) => e.target.parentElement.style.background = idx % 2 === 0 ? "#fff" : "#f8f9fa"}
              >
                {headers.map((h) => (
                  <td key={h + idx} style={{
                    padding: "12px 15px",
                    color: "#333",
                    fontSize: "14px"
                  }}>
                    {row[h]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="mt-3" style={{
          padding: "12px 15px",
          background: "#f8f9fa",
          borderRadius: "8px",
          textAlign: "right"
        }}>
          <small style={{ color: "#666", fontWeight: "500" }}>
            Showing {filteredData.length} of {data.length} records
          </small>
        </div>
      </div>
    );
  };

  return (
    <div className="animated fadeIn">
      {(block || dateLoading) && <LoadingOverlay />}
      <Card style={{
        borderRadius: "15px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        border: "none",
        overflow: "hidden"
      }}>
        <CardHeader style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "20px 25px",
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: "none"
        }}>
          <i className="fa fa-cash" style={{ fontSize: "24px", color: "#fff" }}></i>
          <strong style={{ fontSize: '1.2rem', color: "#fff" }}>Cash Details Center</strong>
        </CardHeader>
        <CardBody style={{ padding: "25px" }}>
          <Nav tabs style={{ borderBottom: "2px solid #f0f0f0", paddingBottom: "15px" }}>
            <NavItem>
              <NavLink
                className={activeTab === "1" ? "active" : ""}
                style={{
                  cursor: "pointer",
                  color: activeTab === "1" ? "#667eea" : "#999",
                  borderBottom: activeTab === "1" ? "3px solid #667eea" : "none",
                  fontWeight: activeTab === "1" ? "600" : "500",
                  transition: "all 0.3s ease"
                }}
                onClick={() => toggleTab("1")}
              >
                <i className="fa fa-upload" style={{ marginRight: "8px" }}></i>
                Upload CSV
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "2" ? "active" : ""}
                style={{
                  cursor: "pointer",
                  color: activeTab === "2" ? "#667eea" : "#999",
                  borderBottom: activeTab === "2" ? "3px solid #667eea" : "none",
                  fontWeight: activeTab === "2" ? "600" : "500",
                  transition: "all 0.3s ease"
                }}
                onClick={() => toggleTab("2")}
              >
                <i className="fa fa-calendar" style={{ marginRight: "8px" }}></i>
                Date Filter
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-3">
            <TabPane tabId="1">
              <Card style={{
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}>
                <CardBody style={{ padding: "25px" }}>
                  <FormGroup row>
                    <Col md="3" style={{ marginBottom: "15px" }}>
                      <label style={{ fontWeight: "600", color: "#333", display: "flex", alignItems: "center", gap: "8px" }}>
                        <i className="fa fa-file-csv" style={{ color: "#667eea" }}></i>
                        Upload CSV
                      </label>
                    </Col>
                    <Col xs="12" md="6" style={{ marginBottom: "15px" }}>
                      <Input
                        type="file"
                        name="file"
                        id="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{
                          borderRadius: "8px",
                          border: "2px solid #e0e0e0",
                          padding: "10px 12px",
                          transition: "all 0.3s ease",
                          cursor: "pointer"
                        }}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup row style={{ marginTop: "20px" }}>
                    <Col md="3" />
                    <Col md="3" style={{ marginBottom: "10px" }}>
                      <Button
                        onClick={handleUpload}
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 20px",
                          color: "#fff",
                          fontWeight: "600",
                          cursor: "pointer",
                          width: "100%",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)"
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow = "0 6px 16px rgba(102, 126, 234, 0.6)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.4)";
                        }}
                      >
                        <i className="fa fa-check" style={{ marginRight: "8px" }}></i>
                        Submit
                      </Button>
                    </Col>
                    <Col md="3" style={{ marginBottom: "10px" }}>
                      <Button
                        onClick={clearAll}
                        style={{
                          background: "#ffc107",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 20px",
                          color: "#fff",
                          fontWeight: "600",
                          cursor: "pointer",
                          width: "100%",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 12px rgba(255, 193, 7, 0.4)"
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow = "0 6px 16px rgba(255, 193, 7, 0.6)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 4px 12px rgba(255, 193, 7, 0.4)";
                        }}
                      >
                        <i className="fa fa-times" style={{ marginRight: "8px" }}></i>
                        Cancel
                      </Button>
                    </Col>
                  </FormGroup>
                </CardBody>
              </Card>

              {responseData.length > 0 && (
                <Card style={{
                  marginTop: "25px",
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                }}>
                  <CardHeader style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    padding: "15px 20px",
                    borderBottom: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <i className="fa fa-table" style={{ color: "#fff", fontSize: "18px" }}></i>
                    <strong style={{ color: "#fff" }}>Backend Response</strong>
                  </CardHeader>
                  <CardBody>{renderTable(responseData)}</CardBody>
                </Card>
              )}
            </TabPane>

            <TabPane tabId="2">
              <Card style={{
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}>
                <CardBody style={{ padding: "25px" }}>
                  <FormGroup row>
                    <Col md="3" style={{ marginBottom: "15px", display: "flex", alignItems: "center" }}>
                      <label style={{ fontWeight: "600", color: "#333", display: "flex", alignItems: "center", gap: "8px", margin: 0 }}>
                        <i className="fa fa-calendar-o" style={{ color: "#667eea" }}></i>
                        From Date
                      </label>
                    </Col>
                    <Col xs="12" md="3" style={{ marginBottom: "15px" }}>
                      <Input
                        type="date"
                        name="frmDate"
                        value={dateFilter.frmDate}
                        onChange={handleDateChange}
                        style={{
                          borderRadius: "8px",
                          border: "2px solid #e0e0e0",
                          padding: "10px 12px",
                          transition: "all 0.3s ease",
                          fontSize: "14px"
                        }}
                      />
                    </Col>
                    <Col md="2" style={{ marginBottom: "15px", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                      <label style={{ fontWeight: "600", color: "#333", display: "flex", alignItems: "center", gap: "8px", margin: 0 }}>
                        <i className="fa fa-calendar-o" style={{ color: "#667eea" }}></i>
                        To Date
                      </label>
                    </Col>
                    <Col xs="12" md="3" style={{ marginBottom: "15px" }}>
                      <Input
                        type="date"
                        name="toDate"
                        value={dateFilter.toDate}
                        onChange={handleDateChange}
                        style={{
                          borderRadius: "8px",
                          border: "2px solid #e0e0e0",
                          padding: "10px 12px",
                          transition: "all 0.3s ease",
                          fontSize: "14px"
                        }}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup row style={{ marginTop: "25px" }}>
                    <Col md="3" />
                    <Col md="2" style={{ marginBottom: "10px" }}>
                      <Button
                        onClick={handleSearchByDate}
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 20px",
                          color: "#fff",
                          fontWeight: "600",
                          cursor: "pointer",
                          width: "100%",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)"
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow = "0 6px 16px rgba(102, 126, 234, 0.6)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.4)";
                        }}
                      >
                        <i className="fa fa-search" style={{ marginRight: "8px" }}></i>
                        Go
                      </Button>
                    </Col>
                    <Col md="2" style={{ marginBottom: "10px" }}>
                      <Button
                        onClick={() => setDateResponseData([])}
                        style={{
                          background: "#ffc107",
                          border: "none",
                          borderRadius: "8px",
                          padding: "10px 20px",
                          color: "#fff",
                          fontWeight: "600",
                          cursor: "pointer",
                          width: "100%",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 12px rgba(255, 193, 7, 0.4)"
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow = "0 6px 16px rgba(255, 193, 7, 0.6)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 4px 12px rgba(255, 193, 7, 0.4)";
                        }}
                      >
                        <i className="fa fa-times" style={{ marginRight: "8px" }}></i>
                        Clear
                      </Button>
                    </Col>
                  </FormGroup>
                </CardBody>
              </Card>

              {dateResponseData.length > 0 && (
                <Card style={{
                  marginTop: "25px",
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                }}>
                  <CardHeader style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    padding: "15px 20px",
                    borderBottom: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <i className="fa fa-table" style={{ color: "#fff", fontSize: "18px" }}></i>
                    <strong style={{ color: "#fff" }}>Date Search Results</strong>
                  </CardHeader>
                  <CardBody>{renderTable(dateResponseData)}</CardBody>
                </Card>
              )}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
}

export default CashDetailsCenter;



// WEBPACK FOOTER //
// ./src/components/CustomComponents/CashDetailsCenter.js